import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MovieIcon from "@mui/icons-material/MovieOutlined";
import WarningIcon from "@mui/icons-material/WarningAmberRounded";
import { tokens } from "../theme";
import { dimensionLabel, durationLabel } from "@shared/media";
import { searchPoster, type Frame } from "../utils/posterSearch";
import { translateKey, type Translate } from "../i18n/translate";

/**
 * Upload one file into the media library.
 *
 * Everything is decided BEFORE the bytes leave the browser: the type, the size
 * and the shape are all knowable locally, so a file that cannot be accepted is
 * refused in the dialog rather than after a 128 MB round trip that ends in a
 * 413. The measurements taken here are also what the library later filters and
 * labels by - the server has no decoder, so if the browser does not measure it,
 * nobody does.
 */

/** Kept in step with the server cap and the `accept` list below. */
export const MAX_UPLOAD_BYTES = 128 * 1024 * 1024;

const ACCEPTED_IMAGE = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const ACCEPTED_VIDEO = ["video/mp4", "video/webm", "video/quicktime"];
const ACCEPT_ATTR =
  "image/png,image/jpeg,image/gif,image/webp,video/mp4,video/webm,video/quicktime,.png,.jpg,.jpeg,.gif,.webp,.mp4,.m4v,.webm,.mov";

const NAME_MAX = 100;
const META_SEPARATOR = "  •  ";

/**
 * The earliest purge date the picker offers: tomorrow.
 *
 * Today would mean "destroy this within the hour", which nobody types on
 * purpose and which the server refuses anyway (see `uploadMeta.purgeDate`).
 * Keeping the two in step here means the operator never gets a date silently
 * dropped on the way in.
 */
export function tomorrowISO(now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export interface MeasuredFile {
  file: File;
  /** Object URL for the preview. Revoked when the choice is cleared. */
  previewUrl: string;
  isVideo: boolean;
  width: number | null;
  height: number | null;
  durationSeconds: number | null;
  /** A still captured from the clip, as a small JPEG data URI. */
  posterDataUrl: string | null;
}

/**
 * Read a file's shape without uploading it.
 *
 * Resolves with nulls rather than rejecting when the browser cannot decode it:
 * an unmeasurable file is still uploadable, it just lands without a shape, and
 * failing the whole upload over a missing label would be the wrong trade.
 */
export function measure(file: File): Promise<MeasuredFile> {
  const previewUrl = URL.createObjectURL(file);
  const isVideo = file.type.startsWith("video/");
  const base: MeasuredFile = {
    file,
    previewUrl,
    isVideo,
    width: null,
    height: null,
    durationSeconds: null,
    posterDataUrl: null,
  };

  return new Promise((resolve) => {
    // Kept out here so a decoder that stalls part-way still yields the shape
    // it already read rather than nothing.
    let shape: Partial<MeasuredFile> = {};

    // Never leave the dialog waiting on a decoder that has quietly given up.
    // Generous, because the poster hunt may walk several seek points.
    const timer = window.setTimeout(
      () => resolve({ ...base, ...shape }),
      10_000
    );
    const done = (patch: Partial<MeasuredFile>) => {
      window.clearTimeout(timer);
      resolve({ ...base, ...patch });
    };

    if (isVideo) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.muted = true;
      v.onloadedmetadata = () => {
        shape = {
          width: v.videoWidth || null,
          height: v.videoHeight || null,
          durationSeconds: Number.isFinite(v.duration) ? v.duration : null,
        };
        // Seeking is also what makes the browser decode a frame at all, so the
        // hunt for a usable still and the act of getting any still are the
        // same loop.
        const seekTo = (at: number) =>
          new Promise<Frame | null>((settle) => {
            v.onseeked = () => settle(grabFrame(v));
            try {
              v.currentTime = at;
            } catch {
              settle(null);
            }
          });
        void searchPoster(v.duration, seekTo).then((posterDataUrl) =>
          done({ ...shape, posterDataUrl })
        );
      };
      v.onerror = () => done({});
      v.src = previewUrl;
    } else {
      const img = new Image();
      img.onload = () =>
        done({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => done({});
      img.src = previewUrl;
    }
  });
}

/**
 * Draw the current video frame to a small JPEG.
 *
 * This is what a clip's tile actually shows. Doing it here rather than letting
 * a `<video>` tile paint itself is the difference between a thumbnail that
 * always appears and one that appears in some browsers - Safari routinely
 * leaves a metadata-only video blank, which reads as a broken upload.
 *
 * Capped at 320px on the long edge: it is rendered at ~200px, and a full-size
 * frame would put hundreds of KB into a Mongo document for no visible gain.
 */
const POSTER_MAX_EDGE = 320;

function grabFrame(video: HTMLVideoElement): Frame | null {
  try {
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return null;
    const scale = Math.min(1, POSTER_MAX_EDGE / Math.max(w, h));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Sample rather than sum every pixel - a 320px frame is ~100k pixels and
    // this runs several times per upload.
    let total = 0;
    let n = 0;
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 0; i < data.length; i += 4 * 37) {
      total +=
        0.2126 * data[i]! + 0.7152 * data[i + 1]! + 0.0722 * data[i + 2]!;
      n += 1;
    }
    return {
      dataUrl: canvas.toDataURL("image/jpeg", 0.6),
      luminance: n ? total / n : 0,
    };
  } catch {
    // A cross-origin or DRM-tainted canvas throws on export or on read-back. A
    // clip with no poster still uploads; it just falls back to a plain tile.
    return null;
  }
}

/** Why a chosen file cannot be uploaded, in the reader's terms. */
export function rejectionFor(t: Translate, file: File): string | null {
  const accepted = [...ACCEPTED_IMAGE, ...ACCEPTED_VIDEO];
  const unsupported = () =>
    translateKey(t, "content:mediaUploadDialog.rejectedType", {
      hint: translateKey(t, "content:mediaUploadDialog.acceptHint"),
    });
  // Some browsers hand over an empty type for less common containers, so fall
  // back to the extension rather than refusing a file we would have taken.
  const byExt = /\.(png|jpe?g|gif|webp|mp4|m4v|webm|mov)$/i.test(file.name);
  if (file.type && !accepted.includes(file.type) && !byExt) {
    return unsupported();
  }
  if (!file.type && !byExt) {
    return unsupported();
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    const mb = Math.ceil(file.size / (1024 * 1024));
    return translateKey(t, "content:mediaUploadDialog.rejectedSize", { mb });
  }
  return null;
}

function prettyBytes(t: Translate, n: number): string {
  const k = (unit: string, value: string | number) =>
    translateKey(t, "content:mediaGallery.bytes." + unit, { value });
  if (n < 1024) return k("b", n);
  if (n < 1024 * 1024) return k("kb", Math.round(n / 1024));
  return k("mb", (n / (1024 * 1024)).toFixed(1));
}

/** "MP4", "PNG" - the container, as an operator would say it. */
function kindLabel(file: File): string {
  const ext = file.name.split(".").pop();
  if (ext && ext.length <= 4) return ext.toUpperCase();
  return (file.type.split("/")[1] ?? "FILE").toUpperCase();
}

export interface MediaUploadDialogProps {
  open: boolean;
  onClose: () => void;
  /** Resolve when the upload has finished; throw to surface an error. */
  onUpload: (args: {
    file: File;
    name: string;
    width: number | null;
    height: number | null;
    durationSeconds: number | null;
    posterDataUrl: string | null;
    /** `YYYY-MM-DD`, or null when the uploader set no expiry (the default). */
    purgeAt: string | null;
  }) => Promise<void>;
  /** Where it will land, for the subtitle ("This location", a folder…). */
  destination?: string;
}

export default function MediaUploadDialog({
  open,
  onClose,
  onUpload,
  destination,
}: MediaUploadDialogProps) {
  const { t } = useTranslation(["content", "common"]);
  const [name, setName] = useState("");
  /**
   * The automatic purge date. EMPTY BY DEFAULT, and it must stay that way: a
   * pre-filled expiry would eventually delete a customer's lobby loop on a date
   * nobody chose. Opting in is one field; opting out of a default nobody
   * noticed is a black screen.
   */
  const [purgeAt, setPurgeAt] = useState("");
  const [chosen, setChosen] = useState<MeasuredFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const clearChoice = useCallback(() => {
    setChosen((cur) => {
      if (cur) URL.revokeObjectURL(cur.previewUrl);
      return null;
    });
  }, []);

  // Reset between openings, and never leak the object URL.
  useEffect(() => {
    if (open) {
      setName("");
      setPurgeAt("");
      setError(null);
      setBusy(false);
      setDragging(false);
      clearChoice();
    }
    return () => clearChoice();
  }, [open, clearChoice]);

  const choose = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      setError(null);
      const rejection = rejectionFor(t, file);
      if (rejection) {
        clearChoice();
        setError(rejection);
        return;
      }
      const measured = await measure(file);
      clearChoice();
      setChosen(measured);
      // Seed the name from the filename, minus its extension - most uploads
      // want it, and an empty required field for something already known is
      // just typing.
      setName((cur) =>
        cur ? cur : file.name.replace(/\.[^.]+$/, "").slice(0, NAME_MAX)
      );
    },
    [clearChoice, t]
  );

  const submit = async () => {
    if (!chosen) return;
    setBusy(true);
    setError(null);
    try {
      await onUpload({
        file: chosen.file,
        name: name.trim(),
        width: chosen.width,
        height: chosen.height,
        durationSeconds: chosen.durationSeconds,
        posterDataUrl: chosen.posterDataUrl,
        purgeAt: purgeAt || null,
      });
      onClose();
    } catch (e) {
      setError((e as Error).message || t("mediaUploadDialog.uploadFailed"));
    } finally {
      setBusy(false);
    }
  };

  const canUpload = Boolean(chosen) && name.trim().length > 0 && !busy;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="flex-start" sx={{ mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                typography: "titleLg",
                fontWeight: "fontWeightBold",
                color: tokens.ink,
              }}
            >
              {t("mediaUploadDialog.title")}
            </Typography>
            {destination && (
              <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
                {t("mediaUploadDialog.uploadingTo", { destination })}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            disabled={busy}
            aria-label={t("common:buttons.close")}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        {/* Name. */}
        <Typography variant="bodySm" sx={{ color: tokens.inkMuted, mb: 0.5 }}>
          {t("common:labels.name")}
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder={t("mediaUploadDialog.namePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, NAME_MAX))}
          disabled={busy}
          InputProps={{
            endAdornment: (
              <Typography
                variant="bodySm"
                sx={{ color: tokens.inkMuted, pl: 1 }}
                aria-hidden
              >
                {name.length} / {NAME_MAX}
              </Typography>
            ),
          }}
          sx={{ mb: 2.5 }}
        />

        {/*
          Automatic purge date.

          OPTIONAL, and empty unless the uploader says otherwise. Content that
          is only ever meant to be temporary - a summer campaign, a conference
          board - should stop depending on somebody remembering to remove it,
          but a default expiry would be a catastrophe on the files that are not
          temporary. So: opt-in, one field, no default.

          It can be changed or cleared afterwards from the library, which is
          what this gets instead of a grace period once the date passes.
        */}
        <Typography variant="bodySm" sx={{ color: tokens.inkMuted, mb: 0.5 }}>
          {t("mediaUploadDialog.purgeLabel")}
        </Typography>
        <TextField
          size="small"
          fullWidth
          type="date"
          value={purgeAt}
          onChange={(e) => setPurgeAt(e.target.value)}
          disabled={busy}
          inputProps={{ min: tomorrowISO() }}
          data-testid="upload-purge-at"
          helperText={
            purgeAt
              ? t("mediaUploadDialog.purgeHelpSet")
              : t("mediaUploadDialog.purgeHelpEmpty")
          }
          sx={{ mb: 2.5 }}
        />

        {/* Drop zone, or the chosen file. */}
        {!chosen ? (
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              void choose(e.dataTransfer.files?.[0]);
            }}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            sx={{
              border: `1px ${dragging ? "solid" : "dashed"} ${
                dragging ? tokens.brand : tokens.divider
              }`,
              bgcolor: dragging ? tokens.brandTintSoft : "transparent",
              borderRadius: "12px",
              py: 5,
              px: 3,
              textAlign: "center",
              cursor: "pointer",
              transition: "background-color 120ms, border-color 120ms",
              "&:hover": { borderColor: tokens.brand },
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                bgcolor: tokens.brandTintSoft,
                display: "grid",
                placeItems: "center",
                color: tokens.brand,
                mx: "auto",
                mb: 1.5,
              }}
            >
              <CloudUploadIcon />
            </Box>
            <Typography
              sx={{
                typography: "titleSm",
                fontWeight: "fontWeightBold",
                color: tokens.ink,
              }}
            >
              {t("mediaUploadDialog.dropTitle")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
            >
              {t("mediaUploadDialog.dropSubtitle")}
            </Typography>
            <Typography variant="meta" sx={{ color: tokens.inkMuted, mt: 1 }}>
              {t("mediaUploadDialog.acceptHint")}
            </Typography>
          </Box>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            gap={2}
            sx={{
              border: `1px solid ${tokens.divider}`,
              borderRadius: "12px",
              p: 2,
            }}
          >
            <Box
              sx={{
                width: 132,
                height: 92,
                flexShrink: 0,
                borderRadius: "8px",
                overflow: "hidden",
                bgcolor: tokens.rowAlt,
                display: "grid",
                placeItems: "center",
              }}
            >
              {/*
                A clip previews as its CAPTURED POSTER, not as a <video>. A
                metadata-only video element paints nothing in several browsers,
                which reads as a blank square and makes a perfectly good upload
                look broken - and the poster shown here is the exact image the
                library tile will use, so what is previewed is what is stored.
              */}
              {chosen.isVideo && !chosen.posterDataUrl ? (
                <MovieIcon sx={{ color: tokens.inkFaint }} />
              ) : (
                <Box
                  component="img"
                  src={
                    chosen.isVideo ? chosen.posterDataUrl! : chosen.previewUrl
                  }
                  alt=""
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  typography: "titleSm",
                  fontWeight: "fontWeightBold",
                  color: tokens.ink,
                }}
              >
                {name || chosen.file.name}
              </Typography>
              <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
                {[
                  kindLabel(chosen.file),
                  prettyBytes(t, chosen.file.size),
                  chosen.isVideo
                    ? durationLabel(chosen.durationSeconds)
                    : dimensionLabel(chosen.width, chosen.height),
                ]
                  .filter(Boolean)
                  .join(META_SEPARATOR)}
              </Typography>
            </Box>
            <Button
              startIcon={<DeleteOutlineIcon />}
              onClick={clearChoice}
              disabled={busy}
            >
              {t("common:buttons.remove")}
            </Button>
          </Stack>
        )}

        <input
          ref={inputRef}
          type="file"
          hidden
          accept={ACCEPT_ATTR}
          onChange={(e) => {
            void choose(e.target.files?.[0]);
            // Reset so re-picking the SAME file still fires a change event.
            e.target.value = "";
          }}
        />

        {error && (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ mt: 1.5 }}
            role="alert"
          >
            <WarningIcon sx={{ fontSize: 18, color: tokens.error }} />
            <Typography
              variant="bodySm"
              sx={{ fontWeight: "fontWeightSemibold", color: tokens.error }}
            >
              {error}
            </Typography>
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
          <Button onClick={onClose} disabled={busy} variant="outlined">
            {t("common:buttons.cancel")}
          </Button>
          <Button variant="contained" disabled={!canUpload} onClick={submit}>
            {busy
              ? t("mediaUploadDialog.uploading")
              : t("common:buttons.upload")}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
